import Dexie, { type Table } from 'dexie';
import type { Cart, ClientEvent, Site, SyncRejection } from '@chariot/contract';

/**
 * Local durable store.
 *
 * On the phone this — not the server — is the source of truth. A scan is written here
 * before the UI confirms it, so a killed app, a flat battery or a crashed tab cannot lose
 * an operator's work. The network is a background job that drains the outbox.
 */

export type SyncState = 'pending' | 'confirmed';

export interface LocalEvent {
  id: string;
  at: string;
  sync_state: SyncState;
  round_id: string;
  cart_code: string | null;
  /** The event exactly as it will be (or was) sent. Never rewritten. */
  event: ClientEvent;
}

export interface MetaRow {
  key: string;
  value: unknown;
}

export interface LocalRejection extends SyncRejection {
  seen_at: string;
  event: ClientEvent | null;
}

class ChariotDb extends Dexie {
  sites!: Table<Site, string>;
  carts!: Table<Cart, string>;
  events!: Table<LocalEvent, string>;
  meta!: Table<MetaRow, string>;
  rejections!: Table<LocalRejection, string>;

  constructor() {
    super('chariot');
    this.version(1).stores({
      sites: 'code',
      carts: 'code',
      events: 'id, at, sync_state, round_id, cart_code',
      meta: 'key',
      rejections: 'id, seen_at',
    });
  }
}

export const db = new ChariotDb();

export async function getMeta<T>(key: string, fallback: T): Promise<T> {
  const row = await db.meta.get(key);
  return row === undefined ? fallback : (row.value as T);
}

export async function setMeta(key: string, value: unknown): Promise<void> {
  await db.meta.put({ key, value });
}

/**
 * Ask the browser to exempt our data from eviction. IndexedDB is not unconditionally
 * durable: under storage pressure a browser may discard it, which for a field app means
 * losing scans that the operator watched succeed. This is best-effort — it can be denied —
 * so the UI also surfaces the pending count so nothing silently accumulates unsent.
 */
export async function requestPersistence(): Promise<boolean> {
  try {
    if (navigator.storage?.persist) return await navigator.storage.persist();
  } catch {
    /* unsupported or blocked; not fatal */
  }
  return false;
}

export async function storageEstimate(): Promise<{ usage: number; quota: number } | null> {
  try {
    const e = await navigator.storage?.estimate?.();
    if (e) return { usage: e.usage ?? 0, quota: e.quota ?? 0 };
  } catch {
    /* ignore */
  }
  return null;
}
